import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Chip
} from '@mui/material'
import {
  FilterList,
  Clear,
  Download,
  Refresh
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import { useClients } from '../../hooks/useClients'
import RoleGuard from '../Auth/RoleGuard'

const ReportFilters = ({ 
  filters, 
  onFiltersChange, 
  onGenerate, 
  onClear, 
  onExport,
  loading = false,
  showClientFilter = true,
  showStatusFilter = true,
  showDateFilters = true,
  title = "Filtros de Reporte"
}) => {
  const { data: clientsData } = useClients({ page: 1, limit: 100 })

  const handleFilterChange = (field, value) => {
    onFiltersChange({
      ...filters,
      [field]: value
    })
  }

  const handleClear = () => {
    const clearedFilters = {
      startDate: null,
      endDate: null,
      status: '',
      clientId: ''
    }
    onFiltersChange(clearedFilters)
    if (onClear) onClear()
  }

  const hasActiveFilters = () => {
    return filters.startDate || 
           filters.endDate || 
           filters.status || 
           filters.clientId
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {title}
            </Typography>
            {hasActiveFilters() && (
              <Chip 
                label="Filtros activos" 
                color="primary" 
                size="small" 
                variant="outlined" 
              />
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Clear />}
              onClick={handleClear}
              disabled={loading || !hasActiveFilters()}
              size="small"
            >
              Limpiar
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={onGenerate}
              disabled={loading}
              size="small"
            >
              Actualizar
            </Button>
            
            {onExport && (
              <RoleGuard requireAdmin fallback={null}>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={onExport}
                  disabled={loading}
                  size="small"
                >
                  Exportar
                </Button>
              </RoleGuard>
            )}
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Date Filters */}
          {showDateFilters && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Fecha Inicio"
                  value={filters.startDate ? dayjs(filters.startDate) : null}
                  onChange={(date) => handleFilterChange('startDate', date?.toDate())}
                  disabled={loading}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Fecha Fin"
                  value={filters.endDate ? dayjs(filters.endDate) : null}
                  onChange={(date) => handleFilterChange('endDate', date?.toDate())}
                  disabled={loading}
                  minDate={filters.startDate ? dayjs(filters.startDate) : undefined}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Grid>
            </>
          )}

          {/* Status Filter */}
          {showStatusFilter && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Estado"
                  disabled={loading}
                >
                  <MenuItem value="">Todos los estados</MenuItem>
                  <MenuItem value="POR_COBRAR">Por Cobrar</MenuItem>
                  <MenuItem value="PAGADA">Pagada</MenuItem>
                  <MenuItem value="VENCIDA">Vencida</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Client Filter */}
          {showClientFilter && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Cliente</InputLabel>
                <Select
                  value={filters.clientId || ''}
                  onChange={(e) => handleFilterChange('clientId', e.target.value)}
                  label="Cliente"
                  disabled={loading}
                >
                  <MenuItem value="">Todos los clientes</MenuItem>
                  {clientsData?.clients?.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        {/* Quick Date Filters */}
        {showDateFilters && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Filtros rápidos:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const now = new Date()
                  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
                  onFiltersChange({
                    ...filters,
                    startDate: startOfMonth,
                    endDate: now
                  })
                }}
                disabled={loading}
              >
                Este Mes
              </Button>
              
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const now = new Date()
                  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
                  const endLastMonth = new Date(now.getFullYear(), now.getMonth(), 0)
                  onFiltersChange({
                    ...filters,
                    startDate: lastMonth,
                    endDate: endLastMonth
                  })
                }}
                disabled={loading}
              >
                Mes Anterior
              </Button>
              
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const now = new Date()
                  const startOfYear = new Date(now.getFullYear(), 0, 1)
                  onFiltersChange({
                    ...filters,
                    startDate: startOfYear,
                    endDate: now
                  })
                }}
                disabled={loading}
              >
                Este Año
              </Button>
              
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const now = new Date()
                  const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
                  onFiltersChange({
                    ...filters,
                    startDate: last30Days,
                    endDate: now
                  })
                }}
                disabled={loading}
              >
                Últimos 30 días
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default ReportFilters
